import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { loadPuckPage } from "@/api/cms-puck/api";

export type CatalogFlowTarget = "ARTWORKS" | "MULTIPRINTS";

export type CatalogFlowPreviewValue = {
  /** Cible du binding catalogue (DETAIL / LISTING) si la page en a un. */
  target: CatalogFlowTarget | null;
  /** Étape du parcours (LISTING / DETAIL) si connue. */
  step: "LISTING" | "DETAIL" | "DECOUPAGES" | null;
  isLoading: boolean;
};

const CatalogFlowPreviewContext = createContext<CatalogFlowPreviewValue>({
  target: null,
  step: null,
  isLoading: false,
});

/**
 * Provider monté dans `overrides.puck` : lit les `catalogFlowRoles` de la page
 * éditée pour cibler l'aperçu listing / fiche (œuvres vs multiples).
 */
export function CatalogFlowPreviewProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { id = "" } = useParams<{ id: string }>();
  const pageQuery = useQuery({
    queryKey: ["cms-puck", "page", id, "catalog-flow-meta"],
    queryFn: () => loadPuckPage(id),
    enabled: !!id,
    staleTime: 30_000,
  });

  const role = pageQuery.data?.catalogFlowRoles?.[0];
  const value = useMemo<CatalogFlowPreviewValue>(
    () => ({
      target: role?.target ?? null,
      step:
        role?.step === "LISTING" ||
        role?.step === "DETAIL" ||
        role?.step === "DECOUPAGES"
          ? role.step
          : null,
      isLoading: pageQuery.isLoading,
    }),
    [role?.target, role?.step, pageQuery.isLoading],
  );

  return (
    <CatalogFlowPreviewContext.Provider value={value}>
      {children}
    </CatalogFlowPreviewContext.Provider>
  );
}

export function useCatalogFlowPreview(): CatalogFlowPreviewValue {
  return useContext(CatalogFlowPreviewContext);
}
